/**
 * Moves data between the local database and the API.
 *
 * Two halves, always run in this order:
 *
 *  - **drain** pushes queued local changes up. Each entry re-reads its body from the
 *    mirror, so what goes up is the record's current state, not whatever it looked
 *    like when the edit happened.
 *  - **pull** refreshes the mirror from the server, skipping every record the outbox
 *    still holds. That skip is the whole safety story for offline edits: the server
 *    can never overwrite something this device has not finished uploading.
 *
 * Draining first means the pull that follows sees the device's own writes reflected
 * back, and has fewer records to skip.
 *
 * Resolution between devices is last-writer-wins on the whole aggregate, which is
 * what the API already does — it upserts on a client-owned id. Two people recording
 * the same game on two devices will clobber each other; two people recording
 * different games will not.
 */

import { ApiError, api, seg } from '../api/client';
import { Game, PracticeSession } from '../types/curling';
import { CurlingClub } from '../data/curlingClubs';
import type { SavedTeam } from '../utils/storage';
import {
  OutboxEntry,
  OutboxOp,
  RESOURCES,
  Resource,
  StoredPracticeSession,
  db,
  readLastPulledAt,
  writeLastPulledAt,
} from './db';
import * as outbox from './outbox';

// ------------------------------------------------------------------ adapters

/** How one resource is listed, identified and written on the server. */
interface Remote<T> {
  list: () => Promise<T[]>;
  identify: (record: T) => string;
  push: (record: T, accountId?: string) => Promise<unknown>;
  drop: (id: string) => Promise<void>;
}

const accountQuery = (accountId?: string): string =>
  accountId ? `?accountId=${seg(accountId)}` : '';

const REMOTE: { [R in Resource]: Remote<any> } = {
  clubs: {
    list: () => api.get<CurlingClub[]>('/clubs'),
    // Optional only for entries in the static reference list; ones the API returns
    // are always persisted, account-scoped rows and so always carry an id.
    identify: (club: CurlingClub) => club.id!,
    push: (club, accountId) => api.post(`/clubs${accountQuery(accountId)}`, club),
    drop: id => api.delete(`/clubs/${seg(id)}`),
  } satisfies Remote<CurlingClub>,

  savedTeams: {
    list: () => api.get<SavedTeam[]>('/saved-teams'),
    identify: (team: SavedTeam) => team.id,
    push: (team, accountId) => api.post(`/saved-teams${accountQuery(accountId)}`, team),
    drop: id => api.delete(`/saved-teams/${seg(id)}`),
  } satisfies Remote<SavedTeam>,

  games: {
    list: () => api.get<Game[]>('/games'),
    identify: (game: Game) => game.id,
    push: (game, accountId) => api.post(`/games${accountQuery(accountId)}`, game),
    drop: id => api.delete(`/games/${seg(id)}`),
  } satisfies Remote<Game>,

  practiceSessions: {
    list: async () => {
      const sessions = await api.get<PracticeSession[]>('/practice-sessions');
      // gameId is optional on the wire type but is the local key, so drop anything
      // that arrives without one rather than storing an unaddressable row.
      return sessions.filter(
        (s): s is StoredPracticeSession => typeof s.gameId === 'string' && s.gameId !== ''
      );
    },
    identify: (session: StoredPracticeSession) => session.gameId,
    push: session => api.put(`/games/${seg(session.gameId)}/practice`, session),
    drop: id => api.delete(`/games/${seg(id)}/practice`),
  } satisfies Remote<StoredPracticeSession>,
};

// -------------------------------------------------------------------- status

export interface SyncState {
  /** Best guess at reachability: the browser's flag, corrected by real request outcomes. */
  online: boolean;
  syncing: boolean;
  /** Records waiting to upload. */
  pending: number;
  /** Records the server refused for a reason retrying will not fix. */
  blocked: number;
  lastSyncedAt?: number;
  lastError?: string;
  /** True once the mirror has been filled at least once on this device. */
  hydrated: boolean;
}

let state: SyncState = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  syncing: false,
  pending: 0,
  blocked: 0,
  hydrated: false,
};

const listeners = new Set<(next: SyncState) => void>();

export const getSyncState = (): SyncState => state;

export function subscribeToSync(listener: (next: SyncState) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function patch(changes: Partial<SyncState>): void {
  state = { ...state, ...changes };
  listeners.forEach(listener => listener(state));
}

/** Re-reads the queue depth. Called after every local write and every drain. */
export async function refreshCounts(): Promise<void> {
  patch(await outbox.counts());
}

// --------------------------------------------------------------------- drain

type Outcome = 'done' | 'retry' | 'blocked' | 'abort';

/**
 * Decides what a failed upload means.
 *
 * The distinction that matters is retry-versus-block: anything the server will answer
 * the same way tomorrow has to stop consuming attempts and start telling the user,
 * because the alternative is a queue that spins forever and data that never lands.
 */
function classify(error: unknown, op: OutboxOp): Outcome {
  if (!(error instanceof ApiError)) return 'retry';

  // Network-level failure. Nothing else in the queue will fare better.
  if (error.status === 0) return 'abort';

  // The session went while we were away. client.ts has already told the app.
  if (error.isUnauthenticated) return 'abort';

  // Deleting something the server does not have is the outcome we wanted. Covers a
  // record created and deleted entirely offline, which was never uploaded at all.
  if (error.isNotFound && op === 'delete') return 'done';

  if (error.status === 408 || error.status === 429 || error.status >= 500) return 'retry';

  // 403 today (no write access on this account); 402 once data-entry credits exist.
  return 'blocked';
}

/** Pushes queued changes. Returns false if it gave up because the server is unreachable. */
async function drain(): Promise<boolean> {
  const database = await db();

  for (const entry of await outbox.drainable()) {
    const remote = REMOTE[entry.resource];

    try {
      if (entry.op === 'delete') {
        await remote.drop(entry.id);
      } else {
        const record = await database.get(entry.resource, entry.id);
        if (!record) {
          // The row went away under us. Nothing left to upload.
          await outbox.clearIfUnchanged(entry);
          continue;
        }
        await remote.push(record, entry.accountId);
      }
      await outbox.clearIfUnchanged(entry);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);

      switch (classify(error, entry.op)) {
        case 'done':
          await outbox.clearIfUnchanged(entry);
          break;
        case 'blocked':
          await outbox.markBlocked(entry, message);
          break;
        case 'abort':
          await outbox.markFailed(entry, message);
          patch({ online: false, lastError: message });
          return false;
        default:
          await outbox.markFailed(entry, message);
      }
    }
  }

  return true;
}

// ---------------------------------------------------------------------- pull

/** Replaces the mirror with the server's copy, leaving records the outbox holds alone. */
async function pull(): Promise<void> {
  const database = await db();

  for (const resource of RESOURCES) {
    const remote = REMOTE[resource];
    const records = await remote.list();
    const held = await outbox.heldKeys();

    const tx = database.transaction(resource, 'readwrite');
    const store = tx.objectStore(resource);

    const fromServer = new Set<string>();
    for (const record of records) {
      const id = remote.identify(record);
      if (held.has(`${resource}:${id}`)) continue;
      fromServer.add(id);
      await store.put(record);
    }

    // Anything local that the server no longer has was deleted elsewhere — unless
    // this device is the one holding the change, in which case it is ours to push.
    for (const id of await store.getAllKeys()) {
      if (fromServer.has(id) || held.has(`${resource}:${id}`)) continue;
      await store.delete(id);
    }

    await tx.done;
  }

  const at = Date.now();
  await writeLastPulledAt(at);
  patch({ hydrated: true, lastSyncedAt: at });
}

// -------------------------------------------------------------------- engine

let running: Promise<void> | undefined;
let rerun = false;

/**
 * Drains, then pulls.
 *
 * Overlapping calls collapse: a request arriving mid-run sets a flag and the run
 * repeats once, so a shot recorded during a sync is not left waiting for the next
 * timer tick.
 */
export function sync(): Promise<void> {
  if (running) {
    rerun = true;
    return running;
  }

  running = (async () => {
    try {
      do {
        rerun = false;
        patch({ syncing: true });

        const reachable = await drain();
        if (reachable) {
          try {
            await pull();
            patch({ online: true, lastError: undefined });
          } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            const offline = error instanceof ApiError && error.status === 0;
            patch({ online: !offline, lastError: message });
          }
        }

        await refreshCounts();
      } while (rerun);
    } finally {
      patch({ syncing: false });
      running = undefined;
    }
  })();

  return running;
}

/** Puts refused uploads back in the queue and tries again. */
export async function retryBlocked(): Promise<void> {
  await outbox.unblockAll();
  await refreshCounts();
  await sync();
}

// ------------------------------------------------------------------ triggers

/** How often to check in while the app is open and online. */
const POLL_MS = 60_000;

/** Collapses the burst of writes a single shot produces into one sync attempt. */
const NUDGE_MS = 2_000;

let nudge: ReturnType<typeof setTimeout> | undefined;

/**
 * Asks for a sync soon. Safe to call on every write — this is what makes uploads
 * "real time" when there is a connection, and a no-op when there is not.
 */
export function requestSync(): void {
  void refreshCounts();
  if (!state.online) return;

  clearTimeout(nudge);
  nudge = setTimeout(() => void sync(), NUDGE_MS);
}

let started = false;

/**
 * Wires up the automatic triggers and does the first sync. Called once the app knows
 * who is signed in, since every request here needs a session.
 */
export function startSync(): () => void {
  if (started) return () => {};
  started = true;

  const onOnline = () => {
    patch({ online: true });
    void sync();
  };
  const onOffline = () => patch({ online: false });
  const onVisible = () => {
    if (document.visibilityState === 'visible' && navigator.onLine) void sync();
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(() => {
    if (navigator.onLine) void sync();
  }, POLL_MS);

  void (async () => {
    patch({ hydrated: (await readLastPulledAt()) !== undefined });
    await refreshCounts();
    if (navigator.onLine) await sync();
  })();

  return () => {
    started = false;
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
  };
}

/** Re-exported so callers do not have to reach past this module into the queue. */
export type { OutboxEntry };
export const pendingEntries = outbox.all;
